import React, { useCallback, useEffect, useMemo, useState } from "react";

import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import { Box, Divider, Menu, MenuItem, Stack, Typography } from "@mui/material";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { useActionHook } from "hooks";
import { uniqBy } from "lodash";
import { useDealActions } from "store";
import { IDealRoundsWithInvestors } from "store/types";

import { IViewBy } from ".";

interface Props {
  dealId: number;
  viewBy: IViewBy | null;
  setViewBy: (viewBy: IViewBy | null) => void;
  disabled: boolean;
}

export const ViewSelector: React.FC<Props> = ({ dealId, viewBy, setViewBy, disabled }) => {
  const [viewByMenuAnchorEl, setViewByMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [roundSubMenuAnchorEl, setRoundSubMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [hoveredRoundId, setHoveredRoundId] = useState<number | null>(null);

  const { getDealRoundsWithInvestorsAction } = useDealActions();

  const { data: roundsWithInvestorsData, action: getDealRoundsWithInvestors } = useActionHook(
    () => dealId && getDealRoundsWithInvestorsAction({ dealId })
  );

  const roundsWithInvestors: IDealRoundsWithInvestors[] = useMemo(() => {
    return Array.isArray(roundsWithInvestorsData) ? roundsWithInvestorsData : [];
  }, [roundsWithInvestorsData]);

  const investors = useMemo(() => {
    return uniqBy(
      roundsWithInvestors.flatMap((round) => round.investors),
      "id"
    );
  }, [roundsWithInvestors]);

  const onSelectView = (view: IViewBy | null) => {
    setViewBy(view ? view : null);
    setViewByMenuAnchorEl(null);
  };

  const viewName = useMemo(() => {
    if (!viewBy) return "Deal";

    if (viewBy.isRound && viewBy.round && !viewBy.investor) return viewBy.round.name || "";

    if (viewBy.isRound && viewBy.round && viewBy.investor) return `${viewBy.round.name} | ${viewBy.investor.name}`;

    if (!viewBy.isRound && !viewBy.round && viewBy.investor) return viewBy.investor.name || "";
    return "";
  }, [viewBy]);

  const isRoundSelected = useCallback(
    (id: number) => {
      return viewBy && viewBy.isRound && viewBy.round?.id === id;
    },
    [viewBy]
  );

  const isRoundInvestorSelected = useCallback(
    (id: number) => {
      return viewBy && viewBy.isRound && viewBy.round?.id && viewBy.investor?.id === id;
    },
    [viewBy]
  );

  const isInvestorSelected = useCallback(
    (id: number) => {
      return viewBy && !viewBy.isRound && viewBy.round?.id && viewBy.investor?.id === id;
    },
    [viewBy]
  );

  useEffect(() => {
    if (dealId) {
      getDealRoundsWithInvestors();
    }
  }, []);

  return (
    <>
      <MaterialTooltip title={`View by: ${viewName}`} isShow>
        <Stack
          direction="row"
          alignItems="center"
          gap="5px"
          sx={{ cursor: "pointer", padding: "0 10px", borderRadius: "20px", background: "#ececec" }}
          onClick={(event) => {
            if (disabled) return;
            setViewByMenuAnchorEl(event.currentTarget);
          }}
        >
          <Typography sx={{ fontSize: "14px", fontWeight: "400", whiteSpace: "nowrap" }}>View by:</Typography>
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: "700",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: "200px"
            }}
          >
            {viewName}
          </Typography>
        </Stack>
      </MaterialTooltip>
      <Menu
        disablePortal
        anchorEl={viewByMenuAnchorEl}
        open={Boolean(viewByMenuAnchorEl)}
        onClose={() => {
          setViewByMenuAnchorEl(null);
          setRoundSubMenuAnchorEl(null);
          setHoveredRoundId(null);
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left"
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left"
        }}
        PaperProps={{
          sx: {
            "&.MuiPaper-root": {
              minWidth: "200px",
              maxWidth: "200px",
              height: "auto !important",
              maxHeight: "none !important",
              borderRadius: "8px",
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
              border: "1px solid #e0e0e0",
              padding: "8px 0"
            }
          }
        }}
      >
        <Typography
          sx={{
            fontSize: "12px",
            fontWeight: "600",
            textTransform: "uppercase",
            padding: "8px 16px 4px 16px",
            letterSpacing: "0.5px",
            color: "#5d7782"
          }}
        >
          GLOBAL
        </Typography>
        <MenuItem
          sx={{
            padding: "8px 16px",
            fontSize: "14px",
            fontWeight: "400",
            backgroundColor: !viewBy ? "#e3f2fd" : "transparent",
            "&:hover": {
              backgroundColor: !viewBy ? "#e3f2fd" : "#f5f5f5"
            }
          }}
          onClick={() => onSelectView(null)}
        >
          Deal
        </MenuItem>
        {Boolean(roundsWithInvestors.length) && (
          <>
            <Divider sx={{ margin: "8px 15px !important", color: "#e5e9eb" }} />
            <Typography
              sx={{
                fontSize: "12px",
                fontWeight: "600",
                textTransform: "uppercase",
                padding: "8px 16px 4px 16px",
                letterSpacing: "0.5px",
                color: "#5d7782"
              }}
            >
              ROUNDS
            </Typography>
            <Stack sx={{ maxHeight: "200px", overflow: "auto" }}>
              {roundsWithInvestors.map((round) => {
                return (
                  <MenuItem
                    key={round.round_id}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      backgroundColor: isRoundSelected(round.round_id)
                        ? "#e3f2fd"
                        : hoveredRoundId === round.round_id
                          ? "#e3f2fd"
                          : "transparent",

                      "&:hover": {
                        backgroundColor: isRoundSelected(round.round_id) ? "#e3f2fd" : "#f5f5f5"
                      }
                    }}
                    onMouseEnter={(e) => {
                      setHoveredRoundId(round.round_id);
                      setRoundSubMenuAnchorEl(e.currentTarget);
                    }}
                    onMouseLeave={(e) => {
                      // Check if mouse is moving to the submenu
                      const relatedTarget = e.relatedTarget as HTMLElement;
                      // eslint-disable-next-line quotes
                      if (!relatedTarget || !relatedTarget.closest('[data-submenu="true"]')) {
                        setRoundSubMenuAnchorEl(null);
                        setHoveredRoundId(null);
                      }
                    }}
                  >
                    <MaterialTooltip title={round.round_name} isShow>
                      <Typography sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {round.round_name}
                      </Typography>
                    </MaterialTooltip>
                    <ExpandMoreOutlinedIcon sx={{ rotate: "-90deg" }} />
                  </MenuItem>
                );
              })}
            </Stack>
          </>
        )}
        {Boolean(investors.length) && (
          <>
            <Divider sx={{ margin: "8px 15px !important", color: "#e5e9eb" }} />
            <Typography
              sx={{
                fontSize: "12px",
                fontWeight: "600",
                textTransform: "uppercase",
                padding: "8px 16px 4px 16px",
                letterSpacing: "0.5px",
                color: "#5d7782"
              }}
            >
              INVESTORS
            </Typography>
            <Stack sx={{ maxHeight: "300px", overflow: "auto" }}>
              {investors.map((investor) => {
                return (
                  <MenuItem
                    key={investor.id}
                    sx={{
                      padding: "8px 16px",
                      fontSize: "14px",
                      fontWeight: "400",
                      backgroundColor: isInvestorSelected(investor.id) ? "#e3f2fd" : "transparent",
                      "&:hover": {
                        backgroundColor: isInvestorSelected(investor.id) ? "#e3f2fd" : "#f5f5f5"
                      }
                    }}
                    onClick={() =>
                      onSelectView({ investor: { id: investor.id, name: investor.name }, round: null, isRound: false })
                    }
                  >
                    <MaterialTooltip title={investor.name} isShow>
                      <Typography sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {investor.name}
                      </Typography>
                    </MaterialTooltip>
                  </MenuItem>
                );
              })}
            </Stack>
          </>
        )}
      </Menu>
      {Boolean(roundSubMenuAnchorEl) && hoveredRoundId !== null && (
        <Box
          data-submenu="true"
          sx={{
            position: "fixed",
            top: `${(roundSubMenuAnchorEl?.getBoundingClientRect()?.top || 0) - 50}px`,
            left: `${(roundSubMenuAnchorEl?.getBoundingClientRect()?.right || 0) - 10}px`,
            maxWidth: "300px",
            maxHeight: "500px",
            overflow: "auto",
            height: "auto",
            borderRadius: "8px",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
            border: "1px solid #e0e0e0",
            padding: "8px 0",
            backgroundColor: "white",
            zIndex: 1300
          }}
          onMouseLeave={() => {
            setRoundSubMenuAnchorEl(null);
            setHoveredRoundId(null);
          }}
        >
          {hoveredRoundId !== null && (
            <>
              <Typography
                sx={{
                  fontSize: "12px",
                  fontWeight: "600",
                  textTransform: "uppercase",
                  padding: "8px 16px 4px 16px",
                  letterSpacing: "0.5px",
                  color: "#5d7782"
                }}
              >
                INVESTORS
              </Typography>
              <Box
                sx={{
                  padding: "8px 16px",
                  fontSize: "14px",
                  fontWeight: "400",
                  cursor: "pointer",
                  backgroundColor: viewBy?.isRound && viewBy?.round?.id === hoveredRoundId ? "#e3f2fd" : "transparent",

                  "&:hover": {
                    backgroundColor: "#f5f5f5"
                  }
                }}
                onClick={() => {
                  setViewBy({
                    round: {
                      id: hoveredRoundId,
                      name: roundsWithInvestors.find((round) => round.round_id === hoveredRoundId)?.round_name || ""
                    },
                    investor: null,
                    isRound: true
                  });
                  setRoundSubMenuAnchorEl(null);
                  setHoveredRoundId(null);
                  setViewByMenuAnchorEl(null);
                }}
              >
                <Typography sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>All</Typography>
              </Box>
              {roundsWithInvestors
                .find((round) => round.round_id === hoveredRoundId)
                ?.investors.map((investor) => {
                  return (
                    <Box
                      key={investor.id}
                      sx={{
                        padding: "8px 16px",
                        fontSize: "14px",
                        fontWeight: "400",
                        backgroundColor: isRoundInvestorSelected(investor.id) ? "#e3f2fd" : "transparent",
                        cursor: "pointer",
                        "&:hover": {
                          backgroundColor: isRoundInvestorSelected(investor.id) ? "#e3f2fd" : "#f5f5f5"
                        }
                      }}
                      onClick={() => {
                        onSelectView({
                          investor: { id: investor.id, name: investor.name },
                          round: {
                            id: hoveredRoundId,
                            name:
                              roundsWithInvestors.find((round) => round.round_id === hoveredRoundId)?.round_name || ""
                          },
                          isRound: true
                        });
                        setRoundSubMenuAnchorEl(null);
                        setHoveredRoundId(null);
                        setViewByMenuAnchorEl(null);
                      }}
                    >
                      <MaterialTooltip title={investor.name} isShow>
                        <Typography sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {investor.name}
                        </Typography>
                      </MaterialTooltip>
                    </Box>
                  );
                })}
            </>
          )}
        </Box>
      )}
    </>
  );
};
